export const purple = '\x1b[38;2;168;85;247m';
export const green = '\u001b[32m';
export const red = '\x1b[91m';
export const cyan = '\x1b[96m';
export const grey = '\x1b[90m';
export const bold = '\x1b[1m';
export const white = '\x1b[97m';
export const reset = '\x1b[0m';

const ANSI_PATTERN = /\x1b\[[0-9;]*[A-Za-z]/g;

export function stripAnsi(text: string): string {
  return text.replace(ANSI_PATTERN, '');
}

export function visibleWidth(text: string): number {
  return stripAnsi(text).length;
}

export function truncatePlain(text: string, maxLength: number): string {
  const plain = stripAnsi(text);
  return plain.length <= maxLength ? plain : `${plain.slice(0, Math.max(0, maxLength - 1))}\u2026`;
}

type ToolInput = Record<string, unknown> | undefined | null;

const inputOf = (input: ToolInput): Record<string, unknown> =>
  input && typeof input === 'object' ? (input as Record<string, unknown>) : {};

function str(input: ToolInput, key: string): string {
  const value = inputOf(input)[key];
  return typeof value === 'string' ? value : '';
}

/** One-line human label per tool — the ONLY thing shown while a tool runs. */
export function buildToolLabel(name: string, input: ToolInput): string {
  switch (name) {
    case 'read_file':
      return `Read ${str(input, 'path') || '(file)'}`;
    case 'write_file':
      return `Write ${str(input, 'path') || '(file)'}`;
    case 'edit_file':
      return `Edit ${str(input, 'path') || '(file)'}`;
    case 'apply_patch': {
      const patchText = str(input, 'patchText');
      const fileCount = (patchText.match(/\*\*\* (Add|Update|Delete) File:/g) ?? []).length;
      return fileCount > 0 ? `Patch (${fileCount} file)` : 'Patch';
    }
    case 'delete_file':
      return `Delete ${str(input, 'path') || '(file)'}`;
    case 'create_directory':
      return `Mkdir ${str(input, 'path') || '(dir)'}`;
    case 'delete_directory':
      return `Rmdir ${str(input, 'path') || '(dir)'}`;
    case 'run_command':
      return `Run ${truncatePlain(str(input, 'command'), 70) || '(command)'}`;
    case 'list_directory':
      return `List ${str(input, 'path') || '.'}`;
    case 'search_files': {
      const q = str(input, 'query');
      return `Search ${q ? `"${truncatePlain(q, 40)}"` : ''}`.trim();
    }
    case 'grep': {
      const pattern = str(input, 'pattern');
      return `Grep /${truncatePlain(pattern, 36)}/`;
    }
    case 'glob':
      return `Glob ${str(input, 'pattern') || '*'}`;
    case 'web_search': {
      const q = str(input, 'query');
      return `Web "${truncatePlain(q, 44)}"`.trim();
    }
    case 'fetch_web_page':
      return `Fetch ${truncatePlain(str(input, 'url'), 52) || '(url)'}`;
    case 'semantic_search': {
      const q = str(input, 'query');
      return `Semantic ${q ? `"${truncatePlain(q, 40)}"` : ''}`.trim();
    }
    case 'get_project_info':
      return 'Project info';
    case 'get_project_structure':
      return 'Structure';
    case 'git_status':
      return 'Git status';
    case 'git_diff':
      return 'Git diff';
    case 'question':
      return 'Ask user';
    case 'lsp':
      return `LSP ${str(input, 'action') || '?'} ${str(input, 'path')}`.trim();
    case 'skill':
      return `Skill ${str(input, 'name')}`.trim();
    case 'get_code_outline':
      return `Outline ${str(input, 'path')}`.trim();
    case 'create_plan':
      return 'Plan';
    case 'create_tasks':
      return 'Tasks';
    case 'complete_task':
      return `Done ${str(input, 'id')}`.trim();
    default:
      return name;
  }
}

/** Render the instant one-liner shown the moment a tool call starts. */
export function formatToolLine(name: string, input: ToolInput): string {
  return `${bold}${purple}\u23fa${reset} ${white}${buildToolLabel(name, input)}${reset}`;
}

/** Compact red failure line printed under the tool line. */
export function formatToolError(message: string): string {
  return `${red}  \u2514 ${truncatePlain(message.replace(/\r?\n/g, ' '), 180)}${reset}`;
}
